import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import UsageMeter from './UsageMeter';

/**
 * UsageBilling — settings-page section showing the current-period meter
 * + plan summary + per-user/per-case/per-service breakdown tables.
 *
 * Admin only. Non-admin users see UsageMeter directly with scope="me"
 * elsewhere in the UI.
 */
export default function UsageBilling() {
  const [orgUsage, setOrgUsage] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [period, setPeriod] = useState('current');
  const [ceilingDraft, setCeilingDraft] = useState('');
  const [savingCeiling, setSavingCeiling] = useState(false);
  const [ceilingError, setCeilingError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = period && period !== 'current' ? { period } : null;
      const [usageData, planData] = await Promise.all([
        api.get('/usage/org', params),
        api.get('/billing/plan'),
      ]);
      setOrgUsage(usageData);
      setPlan(planData);
    } catch (err) {
      setError(err.message || 'failed to load billing');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const savedCeiling = plan?.spend_ceiling_usd ?? null;
  useEffect(() => {
    setCeilingDraft(savedCeiling != null ? String(savedCeiling) : '');
  }, [savedCeiling]);

  // The org's own monthly ceiling — an org setting, enforced by the spending
  // guard at every spend chokepoint. 0 clears it.
  const saveCeiling = useCallback(async (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) { setCeilingError('Enter a dollar amount.'); return; }
    setSavingCeiling(true);
    setCeilingError(null);
    try {
      const res = await api.post('/org/settings', { ORG_MONTHLY_SPEND_CEILING_USD: amount });
      const outcome = res?.results?.ORG_MONTHLY_SPEND_CEILING_USD;
      if (outcome?.status !== 'updated') throw new Error(outcome?.error || 'Could not save the ceiling');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setCeilingError(err.message || 'Could not save the ceiling');
    } finally {
      setSavingCeiling(false);
    }
  }, []);

  if (loading && !orgUsage) return <div className="card"><h3>Usage & Billing</h3><div>Loading…</div></div>;
  if (error) return <div className="card"><h3>Usage & Billing</h3><div className="error">{error}</div></div>;
  if (!orgUsage || !plan) return null;

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="card usage-billing">
      <h3>Usage & Billing</h3>

      <section className="ub-section">
        <div className="ub-section-head">
          <h4>Period</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              className="meter-period-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              title="Period"
            >
              <option value="current">This month</option>
              <option value="last">Last month</option>
              <option value="all">All time</option>
            </select>
            <button className="btn btn-sm btn-ghost" onClick={() => setRefreshKey((k) => k + 1)}>
              Refresh
            </button>
          </div>
        </div>
        <UsageMeter scope="org" variant="detailed" period={period} refreshKey={refreshKey} />
      </section>

      <section className="ub-section">
        <h4>Monthly spending ceiling</h4>
        <div className="meter-plan-info">
          {savedCeiling != null
            ? `Work pauses when this month's usage reaches ${fmt(savedCeiling)}.`
            : 'No ceiling set.'}
          {' '}At the ceiling, document processing and Arc wait until an admin raises it
          — nothing more is spent in the meantime. It resets on the 1st of each month.
        </div>
        <div className="ub-unlock">
          <label htmlFor="ub-ceiling">Ceiling (USD): </label>
          <input
            id="ub-ceiling"
            type="number"
            min="0"
            step="50"
            value={ceilingDraft}
            onChange={(e) => setCeilingDraft(e.target.value)}
            placeholder="e.g. 500"
          />
          <button
            className="btn btn-sm"
            disabled={savingCeiling || ceilingDraft === '' || Number(ceilingDraft) === (savedCeiling ?? 0)}
            onClick={() => saveCeiling(ceilingDraft)}
          >
            {savingCeiling ? 'Saving…' : 'Save'}
          </button>
          {savedCeiling != null && (
            <button className="btn btn-sm btn-ghost" disabled={savingCeiling} onClick={() => saveCeiling(0)}>
              Remove ceiling
            </button>
          )}
        </div>
        {ceilingError && <div className="error">{ceilingError}</div>}
      </section>

      <section className="ub-section">
        <h4>Plan</h4>
        <table className="ub-plan">
          <tbody>
            <tr><td>Type</td><td><code>{plan.plan_type}</code></td></tr>
            <tr><td>Cycle</td><td>{plan.cycle}</td></tr>
            <tr><td>Cap mode</td><td><code>{plan.cap_mode}</code></td></tr>
            {plan.flat_fee_usd != null && (
              <tr><td>Flat fee</td><td>{fmt(plan.flat_fee_usd)}/mo</td></tr>
            )}
            {plan.included_usage_usd != null && (
              <tr><td>Included usage</td><td>{fmt(plan.included_usage_usd)}/mo</td></tr>
            )}
            {plan.overflow_multiplier !== 1 && (
              <tr><td>Overflow rate</td><td>{plan.overflow_multiplier}× cost</td></tr>
            )}
            {plan.margin_multiplier !== 1 && (
              <tr><td>Margin</td><td>{plan.margin_multiplier}×</td></tr>
            )}
            {plan.temp_unlock_active && (
              <tr>
                <td>Temp unlock</td>
                <td>
                  +{fmt(plan.temp_unlock_usd)} until{' '}
                  {plan.temp_unlock_expires_at
                    ? new Date(plan.temp_unlock_expires_at).toLocaleString()
                    : '—'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {plan.cap_mode === 'hard' && (
          // The plan cap is ChargeStack's limit on the org — shown, not
          // editable here. (The org's own brake is the ceiling above.)
          <div className="meter-plan-info">
            Your plan has a monthly usage limit
            {plan.included_usage_usd != null ? ` of ${fmt(plan.included_usage_usd)}` : ''}
            {' '}set by ChargeStack. Work pauses when it is reached. To change it,
            contact your ChargeStack account manager.
          </div>
        )}
      </section>

      <BreakdownTable title="By user"    rows={orgUsage.by_user}    formatKey={(k) => k.slice(0, 12)} />
      <BreakdownTable title="By case"    rows={orgUsage.by_case}    formatKey={(k) => k.slice(0, 12)} preferLabel />
      <BreakdownTable title="By service" rows={orgUsage.by_service} formatKey={(k) => k} />
    </div>
  );
}

function BreakdownTable({ title, rows, formatKey, preferLabel = false }) {
  if (!rows || rows.length === 0) return null;
  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  return (
    <section className="ub-section">
      <h4>{title}</h4>
      <table className="ub-breakdown">
        <thead>
          <tr><th>{title.replace('By ', '')}</th><th>Cost</th><th>Events</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                {preferLabel && r.label
                  ? r.label
                  : <code>{formatKey(r.key)}</code>}
              </td>
              <td>{fmt(r.total_client_usd)}</td>
              <td>{r.events}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
